import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, PhotoIcon, StarIcon, DocumentTextIcon, MagnifyingGlassIcon, CheckBadgeIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { IProjectAsset, AssetType, CharacterGender, CharacterGenderLabels, Locale, SystemAssetQuery } from '@packages/global';
import { useAssets } from '../../hooks/useAssets';
import { projectAssetApi } from '../../services/user/projectAsset.api';
import { useToast } from '../../stores/toastStore';

interface ProjectAssetSelectorProps {
  isOpen: boolean;
  projectId?: string;
  episodeId?: string;
  onSelect: (asset: IProjectAsset, value: string) => void;
  onClose: () => void;
  initialAspect?: 'image' | 'audio' | 'description';
  initialAssetType?: AssetType; // 指定后只显示该类型，隐藏 tab 栏
}

interface AssetWithFavorite extends IProjectAsset {
  isFavorited?: boolean;
}

const assetTypeOptions = [
  { type: AssetType.CHARACTER, labelKey: 'character' },
  { type: AssetType.PROP, labelKey: 'prop' },
  { type: AssetType.SCENE, labelKey: 'scene' },
];

type AssetScope = 'user' | 'project' | 'episode' | 'system';

// 检查是否为认证角色
const isVerifiedCharacter = (asset: IProjectAsset): boolean => {
  if (asset.assetType !== AssetType.CHARACTER) return false;
  if (!asset.assetId) return false;
  if (!asset.conceptImageUrl) return false;

  // 检查 conceptImageUrl 中是否包含 assetId 参数
  try {
    const url = new URL(asset.conceptImageUrl, window.location.origin);
    const assetIdParam = url.searchParams.get('assetId');
    return !!assetIdParam;
  } catch {
    return false;
  }
};

export default function ProjectAssetSelector({
  isOpen,
  projectId,
  episodeId,
  onSelect,
  onClose,
  initialAspect = 'image',
  initialAssetType,
}: ProjectAssetSelectorProps) {
  const [selectedAssetType, setSelectedAssetType] = useState<AssetType>(
    initialAssetType ?? AssetType.CHARACTER
  );

  const { t, i18n } = useTranslation(['projectSettings', 'common']);

  // 当外部指定类型变化时同步内部状态（弹窗复用时需要）
  useEffect(() => {
    if (initialAssetType) setSelectedAssetType(initialAssetType);
  }, [initialAssetType]);
  const [selectedAsset, setSelectedAsset] = useState<AssetWithFavorite | null>(null);
  const [pendingValue, setPendingValue] = useState<string | null>(null);
  const [activeScope, setActiveScope] = useState<AssetScope>('episode');
  const [favoriting, setFavoriting] = useState(false);
  const { success, error } = useToast();

  // system scope 专用状态
  const [systemAssets, setSystemAssets] = useState<AssetWithFavorite[]>([]);
  const [systemLoading, setSystemLoading] = useState(false);
  const [systemSearch, setSystemSearch] = useState('');
  const [systemSearchInput, setSystemSearchInput] = useState('');
  const [systemPage, setSystemPage] = useState(1);
  const [systemTotalPages, setSystemTotalPages] = useState(1);
  const [systemTotal, setSystemTotal] = useState(0);
  const SYSTEM_PAGE_SIZE = 20;

  // 筛选条件
  const [filterGender, setFilterGender] = useState<CharacterGender | ''>('');
  const [filterCountry, setFilterCountry] = useState('');
  const [filterAgeRange, setFilterAgeRange] = useState(''); // 改为预置区间

  const availableScopes = useMemo(() => {
    const scopes: { id: AssetScope; label: string }[] = [];
    if (episodeId) scopes.push({ id: 'episode', label: t('asset.selector.episodeAssets') });
    if (projectId) scopes.push({ id: 'project', label: t('asset.selector.projectAssets') });
    scopes.push({ id: 'user', label: t('asset.selector.myAssets') });
    scopes.push({ id: 'system', label: t('asset.selector.systemAssets') });
    return scopes;
  }, [projectId, episodeId, t]);

  useEffect(() => {
    if (isOpen) {
      if (episodeId) setActiveScope('episode');
      else if (projectId) setActiveScope('project');
      else setActiveScope('user');
      setSelectedAsset(null);
      setPendingValue(null);
      // 重置搜索状态
      setSystemSearch('');
      setSystemSearchInput('');
      setSystemPage(1);
      // 重置筛选条件
      setFilterGender('');
      setFilterCountry('');
      setFilterAgeRange('');
    }
  }, [isOpen, projectId, episodeId]);

  const useAssetsParams = useMemo(() => {
    // 弹窗关闭时不加载数据
    if (!isOpen) return { enabled: false as const };
    switch (activeScope) {
      case 'episode': return { episodeId };
      case 'project': return { projectId };
      default: return undefined;
    }
  }, [activeScope, episodeId, projectId, isOpen]);

  const { getAssetsByType, loading } = useAssets(useAssetsParams);

  // 加载 system scope 数据：系统主体列表 + 用户已收藏状态
  const loadSystemAssets = useCallback(async (query?: SystemAssetQuery, page: number = 1) => {
    setSystemLoading(true);
    try {
      const [sysAssetsResponse, userAssets] = await Promise.all([
        projectAssetApi.systemAssetPageList({
          query: query || {},
          pagination: {
            page,
            limit: SYSTEM_PAGE_SIZE,
          }
        }),
        projectAssetApi.getUserAssets(),
      ]);

      // 通过 sourceAssetId 建立已收藏映射
      const favoritedIds = new Set(
        userAssets
          .filter((a) => a.sourceAssetId)
          .map((a) => a.sourceAssetId as string)
      );

      const assetsWithFav: AssetWithFavorite[] = sysAssetsResponse.items.map((a) => ({
        ...a,
        isFavorited: favoritedIds.has(a.id),
      }));

      setSystemAssets(assetsWithFav);
      setSystemPage(page);
      setSystemTotalPages(sysAssetsResponse.pagination.totalPages);
      setSystemTotal(sysAssetsResponse.pagination.total);
    } catch (err: any) {
      error(err.message || t('subject.favoriteFailed'));
      setSystemAssets([]);
      setSystemPage(1);
      setSystemTotalPages(1);
      setSystemTotal(0);
    } finally {
      setSystemLoading(false);
    }
  }, [error, t, SYSTEM_PAGE_SIZE]);

  useEffect(() => {
    if (isOpen && activeScope === 'system') {
      // 解析年龄区间
      let ageMin: number | undefined;
      let ageMax: number | undefined;
      if (filterAgeRange) {
        const [min, max] = filterAgeRange.split('-').map(v => parseInt(v));
        ageMin = min;
        ageMax = max;
      }

      const query: SystemAssetQuery = {
        search: systemSearch || undefined,
        assetType: selectedAssetType,
        gender: filterGender || undefined,
        country: filterCountry || undefined,
        ageMin,
        ageMax,
      };
      loadSystemAssets(query, systemPage);
    }
  }, [isOpen, activeScope, systemSearch, selectedAssetType, filterGender, filterCountry, filterAgeRange, systemPage, loadSystemAssets]);

  const handleSystemSearch = () => {
    setSystemSearch(systemSearchInput);
    setSystemPage(1); // 重置到第一页
    setSelectedAsset(null);
    setPendingValue(null);
  };

  if (!isOpen) return null;

  const currentAssets =
    activeScope === 'system'
      ? systemAssets.filter((a) => a.assetType === selectedAssetType)
      : (getAssetsByType(selectedAssetType) as AssetWithFavorite[]);

  const isLoading = activeScope === 'system' ? systemLoading : loading;

  const handleAssetSelect = (asset: AssetWithFavorite) => {
    setSelectedAsset(asset);
    if (initialAspect === 'image') setPendingValue(asset.conceptImageUrl || null);
    else if (initialAspect === 'audio') setPendingValue(asset.voiceReference || null);
    else if (initialAspect === 'description') setPendingValue(asset.description || null);
  };

  const handleConfirm = () => {
    if (selectedAsset && pendingValue) {
      onSelect(selectedAsset, pendingValue);
      onClose();
    }
  };

  const handleFavorite = async (asset: AssetWithFavorite) => {
    if (favoriting || asset.isFavorited) return;
    setFavoriting(true);
    try {
      await projectAssetApi.favoriteAsset(asset.id);
      success(t('subject.favoritedSuccess'));
      // 更新 system scope 中的已收藏状态
      if (activeScope === 'system') {
        setSystemAssets((prev) =>
          prev.map((a) => (a.id === asset.id ? { ...a, isFavorited: true } : a))
        );
      } else {
        asset.isFavorited = true;
      }
    } catch (err: any) {
      const errorCode = err?.response?.data?.code || err?.code;
      if (errorCode === 'HAS_FAV') {
        // 已收藏：切换为已收藏状态并提示
        success(t('subject.favorited'));
        if (activeScope === 'system') {
          setSystemAssets((prev) =>
            prev.map((a) => (a.id === asset.id ? { ...a, isFavorited: true } : a))
          );
        } else {
          asset.isFavorited = true;
        }
      } else {
        error(err.message || t('subject.favoriteFailed'));
      }
    } finally {
      setFavoriting(false);
    }
  };

  const aspectLabel =
    initialAspect === 'image' ? t('asset.selector.designImage') :
      initialAspect === 'audio' ? t('asset.selector.referenceAudio') : t('asset.selector.subjectDescription');

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* 固定高度避免切换抖动 */}
        <div className="bg-bg-surface rounded-lg shadow-xl w-[800px] h-[635px] flex flex-col overflow-hidden">

          {/* Header */}
          <div className="flex-none flex items-center justify-between px-4 py-3 border-b border-border-subtle">
            {availableScopes.length > 1 ? (
              <div className="flex gap-1 bg-bg-elevated p-1 rounded-lg">
                {availableScopes.map(scope => (
                  <button
                    key={scope.id}
                    type="button"
                    onClick={() => {
                      setActiveScope(scope.id);
                      setSelectedAsset(null);
                      setPendingValue(null);
                      // 切换 scope 时重置搜索
                      if (scope.id !== 'system') {
                        setSystemSearch('');
                        setSystemSearchInput('');
                      }
                    }}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-all cursor-pointer ${activeScope === scope.id
                      ? 'bg-bg-surface text-gray-50 shadow-sm'
                      : 'text-gray-400 hover:text-gray-300 hover:bg-bg-surface/50'
                      }`}
                  >
                    {scope.label}
                  </button>
                ))}
              </div>
            ) : (
              <h2 className="text-base font-semibold text-gray-50">
                {availableScopes[0]?.label || t('asset.selector.selectAsset')}
              </h2>
            )}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400 bg-bg-elevated px-2 py-0.5 rounded-full border border-border-subtle">
                {aspectLabel}
              </span>
              <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-300 cursor-pointer">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Asset type tabs — 外部指定类型时隐藏 */}
          {!initialAssetType && (
            <div className="flex-none border-b border-border-subtle flex px-4 bg-bg-surface">
              {assetTypeOptions.map((option) => (
                <button
                  key={option.type}
                  type="button"
                  onClick={() => {
                    setSelectedAssetType(option.type);
                    setSelectedAsset(null);
                    setPendingValue(null);
                    setSystemPage(1); // 切换类型时重置页码
                  }}
                  className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors cursor-pointer ${selectedAssetType === option.type
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-400 hover:text-gray-300'
                    }`}
                >
                  {t(`asset.generateModalV2.subjectType.${option.labelKey}`)}
                </button>
              ))}
            </div>
          )}

          {/* System scope 搜索框和筛选 */}
          {activeScope === 'system' && (
            <div className="flex-none px-3 py-2 border-b border-border-subtle bg-bg-surface">
              <div className="flex gap-2 items-center">
                {/* 筛选条件（仅角色类型显示） */}
                {selectedAssetType === AssetType.CHARACTER && (
                  <>
                    <select
                      value={filterGender}
                      onChange={(e) => {
                        setFilterGender(e.target.value as CharacterGender | '');
                        setSystemPage(1); // 筛选时重置页码
                      }}
                      className="px-2 py-1.5 text-xs bg-bg-form border border-border-subtle rounded text-gray-50 focus:border-border-default focus:outline-none transition-colors"
                    >
                      <option value="">{t('common:gender')}: {t('common:all')}</option>
                      <option value={CharacterGender.MALE}>{CharacterGenderLabels[CharacterGender.MALE][i18n.language as Locale]}</option>
                      <option value={CharacterGender.FEMALE}>{CharacterGenderLabels[CharacterGender.FEMALE][i18n.language as Locale]}</option>
                      <option value={CharacterGender.OTHER}>{CharacterGenderLabels[CharacterGender.OTHER][i18n.language as Locale]}</option>
                    </select>

                    <input
                      type="text"
                      value={filterCountry}
                      onChange={(e) => {
                        setFilterCountry(e.target.value);
                        setSystemPage(1); // 筛选时重置页码
                      }}
                      placeholder={t('common:country')}
                      className="px-2 py-1.5 text-xs bg-bg-form border border-border-subtle rounded text-gray-50 placeholder-gray-500 focus:border-border-default focus:outline-none transition-colors w-24"
                    />

                    <select
                      value={filterAgeRange}
                      onChange={(e) => {
                        setFilterAgeRange(e.target.value);
                        setSystemPage(1); // 筛选时重置页码
                      }}
                      className="px-2 py-1.5 text-xs bg-bg-form border border-border-subtle rounded text-gray-50 focus:border-border-default focus:outline-none transition-colors"
                    >
                      <option value="">{t('common:age')}: {t('common:all')}</option>
                      <option value="0-12">{t('projectSettings:asset.selector.ageRange.child')}</option>
                      <option value="13-17">{t('projectSettings:asset.selector.ageRange.teen')}</option>
                      <option value="18-35">{t('projectSettings:asset.selector.ageRange.youngAdult')}</option>
                      <option value="36-60">{t('projectSettings:asset.selector.ageRange.middleAged')}</option>
                      <option value="61-100">{t('projectSettings:asset.selector.ageRange.senior')}</option>
                    </select>
                  </>
                )}

                {/* 搜索框 */}
                <div className="relative flex-1">
                  <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500 pointer-events-none" />
                  <input
                    type="text"
                    value={systemSearchInput}
                    onChange={(e) => setSystemSearchInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSystemSearch()}
                    placeholder={t('asset.selector.searchPlaceholder')}
                    className="w-full pl-8 pr-3 py-1.5 text-xs bg-bg-form border border-border-subtle rounded-lg text-gray-50 placeholder-gray-500 focus:border-border-default focus:outline-none transition-colors"
                  />
                </div>

                {/* 搜索按钮 */}
                <button
                  type="button"
                  onClick={handleSystemSearch}
                  className="px-3 py-1.5 text-xs rounded-lg bg-bg-elevated border border-border-subtle text-gray-300 hover:text-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t('common:search')}
                </button>

                {/* 清除按钮 */}
                {selectedAssetType === AssetType.CHARACTER && (filterGender || filterCountry || filterAgeRange) && (
                  <button
                    type="button"
                    onClick={() => {
                      setFilterGender('');
                      setFilterCountry('');
                      setFilterAgeRange('');
                      setSystemPage(1); // 清除筛选时重置页码
                    }}
                    className="px-2 py-1.5 text-xs text-gray-400 hover:text-gray-300 transition-colors whitespace-nowrap"
                  >
                    {t('common:clear')}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Main content — flex-1 + min-h-0 保证固定高度内滚动 */}
          <div className="flex-1 min-h-0 overflow-y-auto p-3">
            {isLoading ? (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm">{t('common:loading')}</div>
            ) : currentAssets.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                {t('asset.selector.noAssetType', { type: t(`asset.generateModalV2.subjectType.${assetTypeOptions.find(opt => opt.type === selectedAssetType)?.labelKey || 'default'}`) })}
              </div>
            ) : (
              <div className="grid grid-cols-5 gap-2">
                {currentAssets.map((asset) => (
                  <AssetCard
                    key={asset.id}
                    asset={asset}
                    aspect={initialAspect}
                    isSelected={selectedAsset?.id === asset.id}
                    showFavorite={activeScope !== 'user'}
                    favoriting={favoriting}
                    onSelect={handleAssetSelect}
                    onFavorite={handleFavorite}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Pagination - 仅在 system scope 显示 */}
          {activeScope === 'system' && systemTotalPages > 1 && (
            <div className="flex-none flex items-center justify-center gap-2 px-4 py-2 border-t border-border-subtle bg-bg-surface">
              <button
                type="button"
                onClick={() => setSystemPage(p => Math.max(1, p - 1))}
                disabled={systemPage === 1}
                className="px-3 py-1 text-xs rounded bg-bg-elevated hover:bg-border-default text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {t('common:previous')}
              </button>
              <span className="text-xs text-gray-400">
                {systemPage} / {systemTotalPages} ({systemTotal} {t('common:items')})
              </span>
              <button
                type="button"
                onClick={() => setSystemPage(p => Math.min(systemTotalPages, p + 1))}
                disabled={systemPage === systemTotalPages}
                className="px-3 py-1 text-xs rounded bg-bg-elevated hover:bg-border-default text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {t('common:next')}
              </button>
            </div>
          )}

          {/* Footer */}
          <div className="flex-none flex items-center justify-end gap-2 px-4 py-3 border-t border-border-subtle bg-bg-surface">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-1.5 rounded-lg bg-bg-elevated hover:bg-bg-surface border border-border-subtle text-gray-300 text-sm transition-colors cursor-pointer"
            >
              {t('common:cancel')}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!selectedAsset || !pendingValue}
              className="px-4 py-1.5 rounded-lg bg-primary hover:bg-primary/90 text-white text-sm font-medium transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {t('asset.selector.confirmSelect')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// --- AssetCard ---

interface AssetCardProps {
  asset: AssetWithFavorite;
  aspect: 'image' | 'audio' | 'description';
  isSelected: boolean;
  showFavorite: boolean;
  favoriting: boolean;
  onSelect: (asset: AssetWithFavorite) => void;
  onFavorite: (asset: AssetWithFavorite) => void;
}

function AssetCard({
  asset, aspect, isSelected, showFavorite, favoriting, onSelect, onFavorite,
}: AssetCardProps) {
  const { t } = useTranslation(['projectSettings', 'common']);
  const [playing, setPlaying] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const hasValue =
    aspect === 'image' ? !!asset.conceptImageUrl :
      aspect === 'audio' ? !!asset.voiceReference :
        !!asset.description;

  const isVerified = isVerifiedCharacter(asset);

  useEffect(() => {
    if (!showTooltip) return;
    const handler = () => setShowTooltip(false);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [showTooltip]);

  const togglePlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) { audio.pause(); setPlaying(false); }
    else { audio.play(); setPlaying(true); }
  };

  return (
    <div className="relative group">
      <div
        onClick={() => hasValue && onSelect(asset)}
        role="button"
        tabIndex={hasValue ? 0 : -1}
        onKeyDown={(e) => e.key === 'Enter' && hasValue && onSelect(asset)}
        className={`w-full rounded-lg overflow-hidden border transition-all ${!hasValue
          ? 'opacity-40 cursor-not-allowed'
          : isSelected
            ? 'border-primary ring-1 ring-primary/40 cursor-pointer'
            : 'border-border-subtle hover:border-border-default cursor-pointer'
          }`}
      >
        {/* Thumbnail */}
        <div className="aspect-square bg-bg-elevated relative">
          {asset.conceptImageUrl ? (
            <img src={asset.conceptImageUrl} alt={asset.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <PhotoIcon className="h-7 w-7 text-gray-500" />
            </div>
          )}

          {/* 认证角标 */}
          {isVerified && (
            <div
              className="absolute top-2 right-2 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full p-1 shadow-lg ring-2 ring-yellow-300/50"
              title={t('asset.verified')}
            >
              <CheckBadgeIcon className="h-3.5 w-3.5 text-white drop-shadow-md" />
            </div>
          )}

          {/* Selected checkmark — 左上角 */}
          {isSelected && (
            <div className="absolute top-1 left-1 pointer-events-none">
              <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center shadow">
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
          )}


        </div>

        {/* Name row */}
        <div className={`px-1.5 py-1 flex items-center gap-1 ${isSelected ? 'bg-primary/10' : 'bg-bg-elevated'}`}>
          <div className={`flex-1 text-xs font-medium truncate ${isSelected ? 'text-primary' : 'text-gray-50'}`}>
            {asset.name}
          </div>
          {aspect === 'audio' && asset.voiceReference && (
            <>
              <audio ref={audioRef} src={asset.voiceReference} onEnded={() => setPlaying(false)} />
              <button
                type="button"
                onClick={togglePlay}
                className={`flex-none w-4 h-4 rounded-full flex items-center justify-center transition-colors cursor-pointer ${playing ? 'bg-primary text-white' : 'bg-bg-surface hover:bg-primary/20 text-gray-400 hover:text-primary'
                  }`}
              >
                {playing ? (
                  <svg className="w-2 h-2" fill="currentColor" viewBox="0 0 24 24">
                    <rect x="6" y="5" width="4" height="14" rx="1" />
                    <rect x="14" y="5" width="4" height="14" rx="1" />
                  </svg>
                ) : (
                  <svg className="w-2 h-2 ml-px" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Favorite button — 右下角，避免与选中标记重叠 */}
      {showFavorite && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onFavorite(asset); }}
          disabled={favoriting || asset.isFavorited}
          className="absolute bottom-7 right-1 p-0.5 rounded bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50 cursor-pointer hover:bg-black/70 z-10"
          title={asset.isFavorited ? t('projectSettings:subject.favorited') : t('projectSettings:subject.favoriteToLibrary')}
        >
          {asset.isFavorited
            ? <StarIconSolid className="h-3 w-3 text-yellow-400" />
            : <StarIcon className="h-3 w-3 text-yellow-400" />
          }
        </button>
      )}

      {/* Description tooltip */}
      {aspect === 'description' && asset.description && (
        <div className="absolute bottom-0 left-0 right-0 z-20">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setShowTooltip(v => !v); }}
            className="w-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1 bg-black/65 hover:bg-black/80 text-white text-xs py-0.5 rounded-b-lg cursor-pointer"
          >
            <DocumentTextIcon className="h-3 w-3" />
            {t('asset.selector.preview')}
          </button>
          {showTooltip && (
            <div
              ref={tooltipRef}
              className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 bg-bg-elevated border border-border-default rounded-lg shadow-xl p-3 z-30"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="absolute -bottom-[5px] left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-bg-elevated border-r border-b border-border-default rotate-45" />
              <div className="text-xs font-semibold text-gray-200 mb-1.5 truncate">{asset.name}</div>
              <p className="text-xs text-gray-400 leading-relaxed line-clamp-6 whitespace-pre-wrap">
                {asset.description}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
